import React from 'react';
import styled from 'styled-components';

import MediaQuery from 'shared/components/media-query';
import useUI from 'src/hooks/use-ui';
import { useRemoteComponent, RemoteComponentSlot } from 'hooks/use-remote-component';
import { Navigation } from 'src/dispensary/core-menu/components/header/navigation';
import { BlackSparkleIcon } from 'src/assets/black-sparkle-icon';
import { HIGHLIGHT_GOLD } from 'src/dispensary/core-menu/components/header/concierge-nav-button/concierge-button-effects';
import { useConciergeNavigate } from 'src/dispensary/core-menu/components/header/concierge-nav-button/use-concierge-navigate';
import { useConciergeDisplayName } from 'src/concierge/use-concierge-display-name';
import { tracker } from 'src/utils/analytics';

type KioskConciergeEntryProps = {
  cName: string | null;
  /** `storeSettings.enableKioskConcierge` — resolved by the caller (the layout). */
  enabled: boolean;
};

/**
 * Kiosk concierge entry rendered in the layout shell, outside `<Header>`, so it
 * stays available whatever the header's navigation slot resolves to.
 *
 * With the plain fallback `Navigation` it renders only below 880px: from 880px up,
 * `DesktopHeader`'s `ConciergeNavButton` is the entry and this one would duplicate it.
 * A custom or remote navigation may lack its own entry, so it renders at every width there.
 *
 * Hidden on the active concierge route (`isConciergeActive`).
 */
export const KioskConciergeEntry = ({ cName, enabled }: KioskConciergeEntryProps): JSX.Element | null => {
  const { isKiosk, isConciergeActive } = useUI();
  const displayName = useConciergeDisplayName();
  const navigateToConcierge = useConciergeNavigate();
  const ResolvedNavigation = useRemoteComponent(RemoteComponentSlot.Navigation, { fallback: Navigation });
  const isCustomNav = ResolvedNavigation !== Navigation;

  if (!enabled || !isKiosk || !cName || isConciergeActive) {
    return null;
  }

  const handleClick = (): void => {
    tracker.conciergeEntryPointClicked({ location: 'kiosk_shell' });
    navigateToConcierge();
  };

  const button = (
    <EntryButton type='button' onClick={handleClick} data-testid='kiosk-concierge-entry'>
      <IconWrap aria-hidden>
        <BlackSparkleIcon width={20} height={20} fill={HIGHLIGHT_GOLD} />
      </IconWrap>
      {displayName ? `Ask ${displayName}` : 'AI Concierge'}
    </EntryButton>
  );

  // Custom/remote navigation might not carry its own concierge entry — stay on
  // screen at every width. The plain fallback nav's DesktopHeader already covers
  // >=880px, so only render below that breakpoint there (the H4 iPad-portrait case).
  if (isCustomNav) {
    return button;
  }

  return <MediaQuery mediaQuery='@media (max-width: 879px)'>{button}</MediaQuery>;
};

const IconWrap = styled.span`
  align-items: center;
  display: flex;
`;

/* Full-width kiosk tap target with fixed sizing. */
const EntryButton = styled.button`
  align-items: center;
  background: var(--dcm-color-primary, #0b99e6);
  border: none;
  border-radius: var(--dcm-radius-buttons, 9999px);
  color: var(--dcm-cart-btn-text-active, #fff);
  cursor: pointer;
  display: flex;
  font-size: var(--dcm-font-size-md);
  font-weight: var(--dcm-cart-btn-font-weight);
  gap: 8px;
  justify-content: center;
  margin: 12px 16px 0;
  min-height: 48px;
  padding: 12px 20px;
  width: calc(100% - 32px);

  &:focus-visible {
    outline: 2px solid var(--dcm-kiosk-concierge-entry-focus-color, #0075e0);
    outline-offset: 3px;
  }
`;
